import { Injectable, inject } from '@angular/core';
import {
  AuthApiService,
  HTTP_AP_SignIn_WithProvider_Authorize_Response,
  HTTP_AP_SignIn_WithProvider_Initialize_Response,
} from '@api-new/authservice';
import { ENUM_AuthorizationProvider } from '@api-new/common';
import { Store } from '@ngrx/store';
import { LocalStorageKeyEnum } from '@shared/enums/local-storage-key.enum';
import { LoginProviderEnum } from '@shared/enums/login-provider.enum';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { loadAdvisorUserData } from '@shared/store/platform/platform.actions';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { selectAuthUserUid } from '../store/auth/auth.selectors';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly ACCESS_TOKEN = 'accessToken';
  private readonly store = inject(Store);
  private readonly authApiService = inject(AuthApiService);
  private readonly localStorageService = inject(LocalStorageService);

  initAuthData(): void {
    const token = localStorage.getItem(this.ACCESS_TOKEN);
    if (!token) return null;

    this.store.dispatch(loadAdvisorUserData());
  }

  initLoginWithProvider(loginProvider: LoginProviderEnum): Observable<HTTP_AP_SignIn_WithProvider_Initialize_Response> {
    let authorizationProvider: ENUM_AuthorizationProvider;
    switch (loginProvider) {
      case LoginProviderEnum.Google:
        authorizationProvider = ENUM_AuthorizationProvider.AUTHORIZATION_PROVIDER_GOOGLE;
        break;
      case LoginProviderEnum.Mab:
        authorizationProvider = ENUM_AuthorizationProvider.AUTHORIZATION_PROVIDER_MAB;
        break;
      default:
        throw new Error('Invalid SSO provider');
    }
    return this.authApiService.HTTP_AP_SignIn_WithProvider_Initialize({ authorizationProvider });
  }

  authenticateLoginWithProvider(
    loginProvider: LoginProviderEnum,
    authorizationResponseUrl: string,
  ): Observable<HTTP_AP_SignIn_WithProvider_Authorize_Response> {
    let authorizationProvider: ENUM_AuthorizationProvider;
    switch (loginProvider) {
      case LoginProviderEnum.Google:
        authorizationProvider = ENUM_AuthorizationProvider.AUTHORIZATION_PROVIDER_GOOGLE;
        break;
      case LoginProviderEnum.Mab:
        authorizationProvider = ENUM_AuthorizationProvider.AUTHORIZATION_PROVIDER_MAB;
        break;
      default:
        throw new Error('Invalid SSO provider');
    }
    return this.authApiService.HTTP_AP_SignIn_WithProvider_Authorize({ authorizationProvider, authorizationResponseUrl });
  }

  logout(): void {
    localStorage.setItem(this.ACCESS_TOKEN, '');
  }

  getToken(): string {
    return localStorage.getItem(this.ACCESS_TOKEN) || null;
  }

  authUser(): Observable<string> {
    return this.store.select(selectAuthUserUid);
  }

  userId(): Observable<string> {
    return this.authUser().pipe(map((userId) => userId || null));
  }

  setRedirectUrl(redirectUrl: string): void {
    this.localStorageService.setItem(LocalStorageKeyEnum.LoginRedirectUrl, redirectUrl);
  }

  getRedirectUrl(): string {
    return this.localStorageService.getItem(LocalStorageKeyEnum.LoginRedirectUrl);
  }

  clearRedirectUrl(): void {
    this.localStorageService.removeItem(LocalStorageKeyEnum.LoginRedirectUrl);
  }

  setAccessToken(token: string): void {
    localStorage.setItem(this.ACCESS_TOKEN, token);
  }
}
